import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Clock, Play, Square, Plus, Trash2, Calendar as CalendarIcon, BarChart3, ChevronDown, ChevronUp, FolderKanban, Filter, Users, X, Download, FileText, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, startOfMonth, startOfQuarter, startOfYear, endOfWeek, endOfMonth, endOfQuarter, endOfYear, subDays, subWeeks, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useTimeTracking } from '@/contexts/TimeTrackingContext';

const TimeTracking = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { canUseTimeTracking, features } = useSubscription();
  const { activeTimer, elapsedSeconds, startTimer, stopTimer, formatElapsed, checkActiveTimer } = useTimeTracking();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [entries, setEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]); // All entries for admin view
  const [summary, setSummary] = useState(null);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualTaskId, setManualTaskId] = useState('');
  const [personalOverview, setPersonalOverview] = useState(null);
  const [expandedPeriods, setExpandedPeriods] = useState({ week: true, month: false, year: false });
  const [expandedProjects, setExpandedProjects] = useState({});

  // Filter states
  const [filterPreset, setFilterPreset] = useState('week'); // today, week, month, quarter, year, custom
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [filterProject, setFilterProject] = useState('all');
  const [filterPerson, setFilterPerson] = useState('all');
  const [allUsers, setAllUsers] = useState([]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isProjectLeader = user?.role === 'projektleiter';
  const canViewAllEntries = isAdmin || isProjectLeader;
  
  const hasTimeTrackingAccess = canUseTimeTracking();

  // Initialize dates on mount
  useEffect(() => {
    applyFilterPreset('week');
  }, []);

  // Fetch projects and users
  useEffect(() => {
    fetchProjects();
    fetchPersonalOverview();
    if (canViewAllEntries) {
      fetchAllUsers();
      fetchAllTimeEntries();
    }
  }, [canViewAllEntries]);

  // Sync selected project/task with active timer
  useEffect(() => {
    if (activeTimer) {
      if (activeTimer.project_id && !selectedProject) {
        setSelectedProject(activeTimer.project_id);
      }
      if (activeTimer.task_id && !selectedTask) {
        setSelectedTask(activeTimer.task_id);
      }
    }
  }, [activeTimer]);

  // Fetch tasks when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchTasks(selectedProject);
      fetchSummary(selectedProject);
      fetchProjectEntries(selectedProject);
    }
  }, [selectedProject]);

  // Refetch entries when filters change
  useEffect(() => {
    if (canViewAllEntries) {
      fetchAllTimeEntries();
    }
  }, [filterDateFrom, filterDateTo, filterProject, filterPerson]);

  const applyFilterPreset = (preset) => {
    setFilterPreset(preset);
    const now = new Date();
    let from, to;

    switch (preset) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'week':
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        from = startOfMonth(now);
        to = endOfMonth(now);
        break;
      case 'quarter':
        from = startOfQuarter(now);
        to = endOfQuarter(now);
        break;
      case 'year':
        from = startOfYear(now);
        to = endOfYear(now);
        break;
      case 'last7days':
        from = subDays(now, 7);
        to = now;
        break;
      case 'last30days':
        from = subDays(now, 30);
        to = now;
        break;
      case 'custom':
        // Keep existing dates
        return;
      default:
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
    }

    setFilterDateFrom(from);
    setFilterDateTo(to);
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setAllUsers(res.data);
    } catch (err) {
      console.error('Error fetching users', err);
    }
  };

  const fetchAllTimeEntries = async () => {
    if (!canViewAllEntries) return;
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filterDateFrom) params.append('from_date', filterDateFrom.toISOString());
      if (filterDateTo) params.append('to_date', filterDateTo.toISOString());
      if (filterProject && filterProject !== 'all') params.append('project_id', filterProject);
      if (filterPerson && filterPerson !== 'all') params.append('user_id', filterPerson);

      const res = await axios.get(`${API}/time-entries/all?${params.toString()}`);
      setAllEntries(res.data);
    } catch (err) {
      console.error('Error fetching all entries', err);
      // Fallback to empty array
      setAllEntries([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data.filter(p => p.status === 'active'));
    } catch (err) {
      toast.error('Fehler beim Laden der Projekte');
    }
  };

  const fetchTasks = async (projectId) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/tasks`);
      setTasks(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Tasks');
    }
  };

  const fetchSummary = async (projectId) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/time-summary`);
      setSummary(res.data);
    } catch (err) {
      console.error('Error fetching summary', err);
    }
  };

  const fetchProjectEntries = async (projectId) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/time-entries`);
      setEntries(res.data);
    } catch (err) {
      console.error('Error fetching entries', err);
    }
  };

  const fetchPersonalOverview = async () => {
    try {
      const res = await axios.get(`${API}/time-entries/personal-overview`);
      setPersonalOverview(res.data);
    } catch (err) {
      console.error('Error fetching personal overview', err);
    }
  };

  const togglePeriod = (period) => {
    setExpandedPeriods(prev => ({ ...prev, [period]: !prev[period] }));
  };

  const toggleProjectInPeriod = (period, projectId) => {
    const key = `${period}-${projectId}`;
    setExpandedProjects(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatHoursMinutes = (hours) => {
    const totalSeconds = Math.round(hours * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    
    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (s > 0 || parts.length === 0) parts.push(`${s}s`);
    
    return parts.join(' ');
  };

  const clearFilters = () => {
    setFilterProject('all');
    setFilterPerson('all');
    applyFilterPreset('week');
  };

  const hasActiveFilters = filterProject !== 'all' || filterPerson !== 'all' || filterPreset !== 'week';

  // Export to CSV function
  const exportToCSV = () => {
    if (filteredEntries.length === 0) {
      toast.error('Keine Daten zum Exportieren');
      return;
    }

    // CSV headers
    const headers = ['Datum', 'Projekt', 'Task', 'Mitarbeiter', 'Dauer (h)', 'Dauer (min)', 'Kosten (CHF)', 'Stundensatz (CHF)', 'Beschreibung'];
    
    // CSV rows
    const rows = filteredEntries.map(entry => {
      const date = entry.created_at ? format(new Date(entry.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : '-';
      const durationHours = (entry.duration_minutes / 60).toFixed(2);
      const cost = (entry.cost || 0).toFixed(2);
      const hourlyRate = (entry.hourly_rate || 120).toFixed(2);
      
      return [
        date,
        entry.project_name || '-',
        entry.task_title || '-',
        entry.user_name || '-',
        durationHours,
        entry.duration_minutes || 0,
        cost,
        hourlyRate,
        (entry.description || '').replace(/"/g, '""') // Escape quotes
      ];
    });

    // Add summary row
    const totalHours = (filteredTotals.totalMinutes / 60).toFixed(2);
    rows.push([]);
    rows.push(['SUMME', '', '', '', totalHours, filteredTotals.totalMinutes, filteredTotals.totalCost.toFixed(2), '', '']);

    // Create CSV content
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Add BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const dateStr = format(new Date(), 'yyyy-MM-dd', { locale: de });
    const periodStr = filterPreset === 'custom' 
      ? `${format(filterDateFrom, 'ddMMyy')}-${format(filterDateTo, 'ddMMyy')}`
      : filterPreset;
    
    link.href = URL.createObjectURL(blob);
    link.download = `zeiterfassung_${periodStr}_${dateStr}.csv`;
    link.click();
    
    toast.success(`${filteredEntries.length} Einträge exportiert`);
  };

  // Export to PDF function
  const exportToPDF = async () => {
    if (filteredEntries.length === 0) {
      toast.error('Keine Daten zum Exportieren');
      return;
    }

    try {
      // Build query params
      const params = new URLSearchParams();
      if (filterDateFrom) params.append('from_date', filterDateFrom.toISOString());
      if (filterDateTo) params.append('to_date', filterDateTo.toISOString());
      if (filterProject && filterProject !== 'all') params.append('project_id', filterProject);
      if (filterPerson && filterPerson !== 'all') params.append('user_id', filterPerson);

      const response = await axios.get(`${API}/export/time-entries/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const dateStr = format(new Date(), 'yyyy-MM-dd', { locale: de });
      link.href = url;
      link.setAttribute('download', `Zeiterfassung_${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF erfolgreich heruntergeladen!');
    } catch (error) {
      toast.error('Fehler beim Exportieren des PDFs');
    }
  };

  // Filter entries for display
  const getFilteredEntries = () => {
    let entriesToFilter = canViewAllEntries ? allEntries : entries;
    
    // Apply date filter
    if (filterDateFrom && filterDateTo) {
      entriesToFilter = entriesToFilter.filter(entry => {
        const entryDate = new Date(entry.end_time || entry.created_at);
        return entryDate >= filterDateFrom && entryDate <= filterDateTo;
      });
    }

    return entriesToFilter;
  };

  const filteredEntries = getFilteredEntries();

  // Calculate totals from filtered entries
  const filteredTotals = {
    totalMinutes: filteredEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0),
    totalCost: filteredEntries.reduce((sum, e) => sum + (e.cost || 0), 0),
    uniqueUsers: [...new Set(filteredEntries.map(e => e.user_id))].length,
    uniqueProjects: [...new Set(filteredEntries.map(e => e.project_id))].length
  };

  // Handler to start timer using context
  const handleStartTimer = async () => {
    if (!selectedTask) {
      toast.error('Bitte wählen Sie einen Task aus');
      return;
    }
    await startTimer(selectedTask);
  };

  // Handler to stop timer using context
  const handleStopTimer = async () => {
    const stopped = await stopTimer();
    if (stopped) {
      if (selectedProject) {
        fetchSummary(selectedProject);
        fetchProjectEntries(selectedProject);
      }
      fetchPersonalOverview();
      if (canViewAllEntries) {
        fetchAllTimeEntries();
      }
    }
  };

  const handleAddManualEntry = async () => {
    const hours = parseInt(manualHours) || 0;
    const mins = parseInt(manualMinutes) || 0;
    const totalMinutes = hours * 60 + mins;
    
    if (!manualTaskId) {
      toast.error('Bitte wählen Sie einen Task aus');
      return;
    }
    if (totalMinutes <= 0) {
      toast.error('Bitte geben Sie eine gültige Zeit ein');
      return;
    }

    try {
      await axios.post(`${API}/time-entries`, {
        task_id: manualTaskId,
        duration_minutes: totalMinutes,
        description: manualDescription || null
      });
      
      setShowManualDialog(false);
      setManualHours('');
      setManualMinutes('');
      setManualDescription('');
      setManualTaskId('');
      if (selectedProject) {
        fetchSummary(selectedProject);
        fetchProjectEntries(selectedProject);
      }
      fetchPersonalOverview();
      if (canViewAllEntries) {
        fetchAllTimeEntries();
      }
      toast.success('Zeiteintrag hinzugefügt');
    } catch (err) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Zeiteintrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/time-entries/${entryId}`);
      if (selectedProject) {
        fetchSummary(selectedProject);
        fetchProjectEntries(selectedProject);
      }
      fetchPersonalOverview();
      if (canViewAllEntries) {
        fetchAllTimeEntries();
      }
      toast.success('Gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDuration = (minutes) => {
    // Convert to seconds first to handle decimal minutes properly
    const totalSeconds = Math.round(minutes * 60);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    
    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (s > 0 || parts.length === 0) parts.push(`${s}s`);
    
    return parts.join(' ');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateRange = () => {
    if (!filterDateFrom || !filterDateTo) return '';
    return `${format(filterDateFrom, 'dd.MM.yyyy', { locale: de })} - ${format(filterDateTo, 'dd.MM.yyyy', { locale: de })}`;
  };

  // Show upgrade page if user doesn't have access
  if (!hasTimeTrackingAccess) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-[60vh] space-y-6">
          <div className="p-6 bg-muted/50 rounded-full">
            <Lock className="w-16 h-16 text-muted-foreground" />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">Zeiterfassung</h1>
            <p className="text-muted-foreground max-w-md">
              Die Zeiterfassung ist im Pro- oder Business-Abonnement verfügbar. 
              Erfassen Sie Arbeitszeiten für Tasks und generieren Sie detaillierte Reports.
            </p>
            <p className="text-sm text-muted-foreground">
              Aktueller Plan: <strong>{features.plan.toUpperCase()}</strong>
            </p>
          </div>
          <Button onClick={() => navigate('/subscription')} size="lg">
            <Crown className="w-4 h-4 mr-2" />
            Jetzt upgraden
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="time-tracking-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Zeiterfassung</h1>
            <p className="text-muted-foreground">Erfassen Sie Ihre Arbeitszeit pro Task</p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Export Button - only show when there are entries */}
            {canViewAllEntries && filteredEntries.length > 0 && (
              <Button 
                variant="outline" 
                onClick={exportToCSV}
                data-testid="export-csv-button"
              >
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
            )}
            
            {canViewAllEntries && filteredEntries.length > 0 && (
              <Button 
                variant="outline" 
                onClick={exportToPDF}
                data-testid="export-pdf-button"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
            )}
            
            <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Zeit manuell erfassen
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Zeit manuell erfassen</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Projekt</Label>
                  <Select
                    value={selectedProject || ''}
                    onValueChange={(val) => {
                      setSelectedProject(val);
                      setManualTaskId('');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Projekt auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Task</Label>
                  <Select
                    value={manualTaskId}
                    onValueChange={setManualTaskId}
                    disabled={!selectedProject}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Task auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {tasks.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label>Stunden</Label>
                    <Input
                      type="number"
                      min="0"
                      value={manualHours}
                      onChange={(e) => setManualHours(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Minuten</Label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={manualMinutes}
                      onChange={(e) => setManualMinutes(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Beschreibung (optional)</Label>
                  <Input
                    value={manualDescription}
                    onChange={(e) => setManualDescription(e.target.value)}
                    placeholder="Was wurde gemacht?"
                  />
                </div>
                
                <Button onClick={handleAddManualEntry} className="w-full">
                  Hinzufügen
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Active Timer Alert */}
        {activeTimer && (
          <Card className="border-red-500/50 bg-red-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
                  <div>
                    <div className="text-sm font-medium text-red-600">Aktiver Timer</div>
                    <div className="text-xs text-muted-foreground">
                      Projekt: <span className="font-medium">{activeTimer.project_name}</span> • 
                      Task: <span className="font-medium">{activeTimer.task_title}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-mono font-bold text-red-600">
                    {formatElapsed(elapsedSeconds)}
                  </div>
                  <Button variant="destructive" size="lg" onClick={handleStopTimer}>
                    <Square className="w-5 h-5 mr-2" />
                    Timer stoppen
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter Section - For Admin/Projektleiter */}
        {canViewAllEntries && (
          <Card className="bg-muted/30" data-testid="time-filter-section">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filter:</span>
                </div>
                
                {/* Date Preset */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Zeitraum:</span>
                  <Select value={filterPreset} onValueChange={applyFilterPreset}>
                    <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="filter-date-preset">
                      <SelectValue placeholder="Zeitraum wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Heute</SelectItem>
                      <SelectItem value="week">Diese Woche</SelectItem>
                      <SelectItem value="month">Dieser Monat</SelectItem>
                      <SelectItem value="quarter">Dieses Quartal</SelectItem>
                      <SelectItem value="year">Dieses Jahr</SelectItem>
                      <SelectItem value="last7days">Letzte 7 Tage</SelectItem>
                      <SelectItem value="last30days">Letzte 30 Tage</SelectItem>
                      <SelectItem value="custom">Benutzerdefiniert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Date Range */}
                {filterPreset === 'custom' && (
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-xs">
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {filterDateFrom ? format(filterDateFrom, 'dd.MM.yy', { locale: de }) : 'Von'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={filterDateFrom}
                          onSelect={setFilterDateFrom}
                          locale={de}
                        />
                      </PopoverContent>
                    </Popover>
                    <span className="text-xs text-muted-foreground">bis</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 text-xs">
                          <CalendarIcon className="w-3 h-3 mr-1" />
                          {filterDateTo ? format(filterDateTo, 'dd.MM.yy', { locale: de }) : 'Bis'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={filterDateTo}
                          onSelect={setFilterDateTo}
                          locale={de}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                
                {/* Project Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Projekt:</span>
                  <Select value={filterProject} onValueChange={setFilterProject}>
                    <SelectTrigger className="w-[180px] h-8 text-sm" data-testid="filter-project">
                      <SelectValue placeholder="Alle Projekte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Projekte</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Person Filter - Admin only */}
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Person:</span>
                    <Select value={filterPerson} onValueChange={setFilterPerson}>
                      <SelectTrigger className="w-[180px] h-8 text-sm" data-testid="filter-person">
                        <SelectValue placeholder="Alle Personen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Personen</SelectItem>
                        {allUsers.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="h-8 text-xs"
                    data-testid="clear-time-filters-button"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Filter zurücksetzen
                  </Button>
                )}
              </div>
              
              {/* Filter Summary */}
              <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-4 text-sm">
                <span className="text-muted-foreground">
                  <CalendarIcon className="w-4 h-4 inline mr-1" />
                  {formatDateRange()}
                </span>
                <span className="text-muted-foreground">
                  {filteredEntries.length} Einträge
                </span>
                <span className="font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {formatDuration(filteredTotals.totalMinutes)}
                </span>
                <span className="text-muted-foreground">
                  CHF {filteredTotals.totalCost.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                </span>
                {isAdmin && (
                  <span className="text-muted-foreground">
                    <Users className="w-4 h-4 inline mr-1" />
                    {filteredTotals.uniqueUsers} Mitarbeiter
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Time Overview - Week/Month/Year (for non-admins or as secondary view) */}
        {personalOverview && !canViewAllEntries && (
          <Card data-testid="personal-time-overview-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Meine Stundenübersicht
              </CardTitle>
              <CardDescription>Ihre erfassten Stunden nach Zeitraum und Projekt</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {['week', 'month', 'year'].map((periodKey) => {
                  const periodData = personalOverview[periodKey];
                  if (!periodData) return null;
                  
                  const displayedProjects = periodData.projects || [];
                  const displayedTotal = periodData;
                  
                  const periodLabels = {
                    week: { title: 'Diese Woche', icon: '📅' },
                    month: { title: 'Dieser Monat', icon: '📆' },
                    year: { title: 'Dieses Jahr', icon: '📊' }
                  };
                  
                  const isExpanded = expandedPeriods[periodKey];
                  
                  return (
                    <div key={periodKey} className="border rounded-lg overflow-hidden">
                      <div 
                        className="flex items-center justify-between p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => togglePeriod(periodKey)}
                        data-testid={`period-${periodKey}-header`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{periodLabels[periodKey].icon}</span>
                          <div>
                            <div className="font-medium">{periodLabels[periodKey].title}</div>
                            <div className="text-xs text-muted-foreground">
                              {displayedProjects.length} Projekt{displayedProjects.length !== 1 ? 'e' : ''}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-mono font-bold text-xl" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              {formatHoursMinutes(displayedTotal.total_hours)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              CHF {displayedTotal.total_cost?.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                            </div>
                          </div>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                        </div>
                      </div>
                      
                      {isExpanded && displayedProjects.length > 0 && (
                        <div className="border-t bg-background">
                          <div className="divide-y">
                            {displayedProjects.map((project) => {
                              const projectKey = `${periodKey}-${project.project_id}`;
                              const isProjectExpanded = expandedProjects[projectKey];
                              
                              return (
                                <div key={project.project_id}>
                                  <div 
                                    className="flex items-center justify-between p-3 hover:bg-muted/20 cursor-pointer transition-colors"
                                    onClick={() => toggleProjectInPeriod(periodKey, project.project_id)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <FolderKanban className="w-4 h-4 text-primary" />
                                      <div>
                                        <div className="font-medium text-sm">{project.project_name}</div>
                                        <div className="text-xs text-muted-foreground">
                                          {project.tasks?.length || 0} Task{project.tasks?.length !== 1 ? 's' : ''} • CHF {project.hourly_rate}/h
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="text-right">
                                        <div className="font-mono font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                          {formatHoursMinutes(project.hours)}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          CHF {project.cost?.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                                        </div>
                                      </div>
                                      {project.tasks?.length > 0 && (
                                        isProjectExpanded 
                                          ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> 
                                          : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                      )}
                                    </div>
                                  </div>
                                  
                                  {isProjectExpanded && project.tasks?.length > 0 && (
                                    <div className="bg-muted/10 px-4 py-2 border-t">
                                      {project.tasks.map((task) => (
                                        <div 
                                          key={task.task_id}
                                          className="flex items-center justify-between py-1.5 text-sm"
                                        >
                                          <div className="flex items-center gap-2 text-muted-foreground">
                                            <Clock className="w-3 h-3" />
                                            <span className="truncate max-w-[250px]">{task.task_title}</span>
                                          </div>
                                          <div className="font-mono text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                            {formatHoursMinutes(task.hours)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {isExpanded && displayedProjects.length === 0 && (
                        <div className="p-4 text-center text-muted-foreground text-sm">
                          Keine Zeiteinträge in diesem Zeitraum
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Timer Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Timer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-muted-foreground">Projekt</Label>
                <Select
                  value={selectedProject || ''}
                  onValueChange={(val) => {
                    setSelectedProject(val);
                    setSelectedTask(null);
                  }}
                  disabled={!!activeTimer}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Projekt auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-muted-foreground">Task</Label>
                <Select
                  value={selectedTask || ''}
                  onValueChange={setSelectedTask}
                  disabled={!selectedProject || !!activeTimer}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Task auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {tasks.length === 0 && (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">Keine Tasks vorhanden</div>
                    )}
                    {tasks.filter(t => !t.completed).length > 0 && (
                      <>
                        {tasks.filter(t => !t.completed).map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                        ))}
                      </>
                    )}
                    {tasks.filter(t => t.completed).length > 0 && (
                      <>
                        {tasks.filter(t => !t.completed).length > 0 && (
                          <div className="px-2 py-1 text-xs text-muted-foreground border-t mt-1 pt-1">Abgeschlossen</div>
                        )}
                        {tasks.filter(t => t.completed).map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            <span className="text-muted-foreground">{t.title} (erledigt)</span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-3">
                {activeTimer ? (
                  <>
                    <div className="text-center">
                      <div className="text-3xl font-mono font-bold text-primary">
                        {formatElapsed(elapsedSeconds)}
                      </div>
                      <div className="text-xs text-muted-foreground">{activeTimer.task_title}</div>
                    </div>
                    <Button variant="destructive" size="lg" onClick={handleStopTimer}>
                      <Square className="w-5 h-5 mr-2" />
                      Stop
                    </Button>
                  </>
                ) : (
                  <Button size="lg" onClick={handleStartTimer} disabled={!selectedTask}>
                    <Play className="w-5 h-5 mr-2" />
                    Start
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Time Entries Table - Admin view with all entries */}
        {canViewAllEntries && filteredEntries.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Zeiteinträge</CardTitle>
                  <CardDescription>
                    {isAdmin ? 'Alle Zeiteinträge aller Mitarbeiter' : 'Zeiteinträge Ihrer Projekte'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Projekt</TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead>Mitarbeiter</TableHead>
                    <TableHead>Dauer</TableHead>
                    <TableHead>Kosten</TableHead>
                    <TableHead>Beschreibung</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm">{formatDate(entry.created_at)}</TableCell>
                      <TableCell className="text-sm">{entry.project_name}</TableCell>
                      <TableCell className="font-medium">{entry.task_title || 'Unbekannt'}</TableCell>
                      <TableCell>{entry.user_name}</TableCell>
                      <TableCell className="font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {formatDuration(entry.duration_minutes)}
                      </TableCell>
                      <TableCell className="font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        CHF {(entry.cost || 0).toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{entry.description || '-'}</TableCell>
                      <TableCell>
                        {(isAdmin || entry.user_id === user.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {canViewAllEntries && filteredEntries.length === 0 && (
          <Card>
            <CardContent className="pt-8 pb-8 text-center">
              <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Keine Zeiteinträge im gewählten Zeitraum</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default TimeTracking;
